import { z } from "zod";

/**
 * Contratos da midia paga (Meta Ads e Google Ads). Token e refresh token so
 * ENTRAM e nunca saem, nem mascarados.
 */

/** O que conta como "lead no gerenciador". */
export const CRITERIOS_DE_LEAD = ["formulario", "conversa"] as const;
export const criterioDeLeadSchema = z.enum(CRITERIOS_DE_LEAD);
export type CriterioDeLead = z.infer<typeof criterioDeLeadSchema>;

export const ROTULO_DO_CRITERIO: Readonly<Record<CriterioDeLead, string>> = {
  formulario: "Cadastros (formulário e site)",
  conversa: "Conversas iniciadas (WhatsApp e Direct)",
};

export const contaDeAnuncioSchema = z.object({
  id: z.string(),
  nome: z.string(),
  moeda: z.string(),
  /** Conta ativa no Meta (status 1). */
  ativa: z.boolean(),
});
export type ContaDeAnuncio = z.infer<typeof contaDeAnuncioSchema>;

export const listaDeContasDeAnuncioSchema = z.object({ dados: z.array(contaDeAnuncioSchema) });
export type ListaDeContasDeAnuncio = z.infer<typeof listaDeContasDeAnuncioSchema>;

/** Acesso da Monte ao Meta (um so para todas as clinicas). */
export const credencialMetaSchema = z.object({
  configurada: z.boolean(),
  /** Quem o token representa no Meta ("Junior Mendes", usuario do sistema...). */
  nomeNoMeta: z.string().nullable(),
  atualizadaEm: z.iso.datetime().nullable(),
});
export type CredencialMeta = z.infer<typeof credencialMetaSchema>;

export const salvarCredencialMetaSchema = z
  .object({ token: z.string().trim().min(20, "token inválido").max(2000, "token inválido") })
  .strict();
export type SalvarCredencialMeta = z.infer<typeof salvarCredencialMetaSchema>;

export const SITUACOES_DA_MIDIA = ["nao-conectada", "conectada", "sincronizando", "com-erro"] as const;

export const conexaoMetaSchema = z.object({
  situacao: z.enum(SITUACOES_DA_MIDIA),
  /** Ids das contas de anuncio da clinica (sem o "act_"). */
  contas: z.array(z.string()),
  criterios: z.array(criterioDeLeadSchema),
  ultimaSincronizacaoEm: z.iso.datetime().nullable(),
  ultimoErro: z.string().nullable(),
});
export type ConexaoMeta = z.infer<typeof conexaoMetaSchema>;

export const salvarConexaoMetaSchema = z
  .object({
    contas: z
      .array(z.string().regex(/^\d{5,25}$/, "conta de anúncio inválida"))
      .min(1, "escolha ao menos uma conta de anúncio")
      .max(20),
    criterios: z.array(criterioDeLeadSchema).min(1, "escolha o que conta como lead"),
  })
  .strict();
export type SalvarConexaoMeta = z.infer<typeof salvarConexaoMetaSchema>;

const idDeContaGoogle = z
  .string()
  .transform((valor) => valor.replace(/\D/g, ""))
  .pipe(z.string().regex(/^\d{8,12}$/, "conta do Google Ads inválida"));

const loginCustomerIdSchema = z
  .string()
  .trim()
  .transform((valor) => valor.replace(/\D/g, ""))
  .pipe(z.union([z.literal(""), z.string().regex(/^\d{8,12}$/, "conta gerente inválida")]))
  .optional();

/** Popup do Google volta para esta rota do painel (query `code` + `state`). */
export const CAMINHO_OAUTH_GOOGLE = "/configuracoes/conexoes/google";

/** Acesso da Monte ao Google Ads (um so para todas as clinicas). */
export const credencialGoogleSchema = z.object({
  configurada: z.boolean(),
  nomeNoGoogle: z.string().nullable(),
  atualizadaEm: z.iso.datetime().nullable(),
});
export type CredencialGoogle = z.infer<typeof credencialGoogleSchema>;

export const salvarCredencialGoogleSchema = z
  .object({
    refreshToken: z.string().trim().min(20, "token inválido").max(2000, "token inválido"),
    /** Conta gerente (MCC), so digitos ou com hifen. */
    loginCustomerId: loginCustomerIdSchema,
  })
  .strict();
export type SalvarCredencialGoogle = z.infer<typeof salvarCredencialGoogleSchema>;

export const iniciarOauthGoogleSchema = z.object({ url: z.string().url() });
export type IniciarOauthGoogle = z.infer<typeof iniciarOauthGoogleSchema>;

export const concluirOauthGoogleSchema = z
  .object({
    code: z.string().trim().min(8, "código inválido").max(4000, "código inválido"),
    state: z.string().trim().min(8, "estado inválido").max(4000, "estado inválido"),
    loginCustomerId: loginCustomerIdSchema,
  })
  .strict();
export type ConcluirOauthGoogle = z.infer<typeof concluirOauthGoogleSchema>;

export const conexaoGoogleSchema = z.object({
  situacao: z.enum(SITUACOES_DA_MIDIA),
  contas: z.array(z.string()),
  ultimaSincronizacaoEm: z.iso.datetime().nullable(),
  ultimoErro: z.string().nullable(),
});
export type ConexaoGoogle = z.infer<typeof conexaoGoogleSchema>;

export const salvarConexaoGoogleSchema = z
  .object({
    contas: z.array(idDeContaGoogle).min(1, "escolha ao menos uma conta de anúncio").max(20),
  })
  .strict();
export type SalvarConexaoGoogle = z.infer<typeof salvarConexaoGoogleSchema>;

/* ---------------------------------------------------------------------------
   Monitoramento de saldo das contas de anuncio (so equipe Monte).
   "Saldo" muda de sentido conforme a forma de pagamento da conta; `medida`
   diz o que o numero representa.
   --------------------------------------------------------------------------- */

export const PLATAFORMAS_DE_MIDIA = ["meta", "google"] as const;
export const plataformaDeMidiaSchema = z.enum(PLATAFORMAS_DE_MIDIA);
export type PlataformaDeMidia = z.infer<typeof plataformaDeMidiaSchema>;

/**
 * - `saldo-pre-pago`: Meta pre-pago (Pix/boleto), saldo disponivel.
 * - `limite-de-gastos`: Meta no cartao com limite de gastos: quanto falta.
 * - `orcamento-da-conta`: Google com faturamento mensal: quanto resta do orcamento.
 * - `indisponivel`: a plataforma nao expoe saldo para esta conta.
 */
export const MEDIDAS_DE_SALDO = ["saldo-pre-pago", "limite-de-gastos", "orcamento-da-conta", "indisponivel"] as const;
export type MedidaDeSaldo = (typeof MEDIDAS_DE_SALDO)[number];

export const ROTULO_DA_MEDIDA: Readonly<Record<MedidaDeSaldo, string>> = {
  "saldo-pre-pago": "Saldo pré-pago",
  "limite-de-gastos": "Até o limite de gastos",
  "orcamento-da-conta": "Orçamento da conta",
  indisponivel: "Sem saldo medido",
};

/** Ordem tambem e prioridade: o que pede acao vem primeiro. */
export const SITUACOES_DO_SALDO = [
  "pagamento-pendente",
  "abaixo-do-minimo",
  "sem-saldo-medido",
  "sem-minimo",
  "ok",
  "inativa",
  /** A Monte tirou a conta do monitoramento: nunca gera alerta. */
  "desativada",
] as const;
export type SituacaoDoSaldo = (typeof SITUACOES_DO_SALDO)[number];

export const saldoDaContaSchema = z.object({
  plataforma: plataformaDeMidiaSchema,
  contaId: z.string(),
  nome: z.string(),
  moeda: z.string(),
  medida: z.enum(MEDIDAS_DE_SALDO),
  saldoCentavos: z.number().int().nullable(),
  /** Minimo que vale para a conta: o proprio ou o padrao. */
  minimoCentavos: z.number().int().nullable(),
  /** `true` quando a conta tem minimo proprio (sobrescreve o padrao). */
  minimoProprio: z.boolean(),
  situacao: z.enum(SITUACOES_DO_SALDO),
  /** Clinicas do painel que usam a conta. */
  clientes: z.array(z.uuid()),
  /** `false`: a Monte desativou a conta no monitoramento (sem alerta). */
  monitorada: z.boolean(),
  observacao: z.string().nullable(),
});
export type SaldoDaConta = z.infer<typeof saldoDaContaSchema>;

export const falhaDeLeituraSchema = z.object({ plataforma: plataformaDeMidiaSchema, mensagem: z.string() });
export type FalhaDeLeitura = z.infer<typeof falhaDeLeituraSchema>;

export const monitoramentoDeSaldosSchema = z.object({
  minimoPadraoCentavos: z.number().int().nullable(),
  lidoEm: z.iso.datetime().nullable(),
  contas: z.array(saldoDaContaSchema),
  falhas: z.array(falhaDeLeituraSchema),
});
export type MonitoramentoDeSaldos = z.infer<typeof monitoramentoDeSaldosSchema>;

const centavosSchema = z
  .number()
  .int("informe o valor em centavos")
  .min(0, "o mínimo não pode ser negativo")
  .max(1_000_000_000_00, "valor alto demais");

export const salvarMinimosDeSaldoSchema = z
  .object({
    minimoPadraoCentavos: centavosSchema.nullable(),
    /** Minimos proprios. Conta fora da lista usa o padrao. */
    porConta: z
      .array(
        z
          .object({
            plataforma: plataformaDeMidiaSchema,
            contaId: z.string().trim().min(1).max(40),
            minimoCentavos: centavosSchema,
          })
          .strict(),
      )
      .max(2000),
  })
  .strict();
export type SalvarMinimosDeSaldo = z.infer<typeof salvarMinimosDeSaldoSchema>;

/** Liga ou desliga o alerta de uma conta (ex.: conta que a clinica paga por fora). */
export const definirMonitoramentoDaContaSchema = z
  .object({
    plataforma: plataformaDeMidiaSchema,
    contaId: z.string().trim().min(1).max(40),
    monitorada: z.boolean(),
  })
  .strict();
export type DefinirMonitoramentoDaConta = z.infer<typeof definirMonitoramentoDaContaSchema>;

/** Chave de IA da Monte. A chave em si nunca volta. */
export const chaveDeIaSchema = z.object({
  configurada: z.boolean(),
  nome: z.string().nullable(),
  atualizadaEm: z.iso.datetime().nullable(),
});
export type ChaveDeIa = z.infer<typeof chaveDeIaSchema>;

export const salvarChaveDeIaSchema = z
  .object({
    chave: z.string().trim().min(20, "chave inválida").max(2000, "chave inválida"),
  })
  .strict();
export type SalvarChaveDeIa = z.infer<typeof salvarChaveDeIaSchema>;
